import React from 'react'
import { Button, Checkbox, DatePicker, Input, Select, Space, Spin, Tag } from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { DebouncedSelect } from '../../components/DebouncedSelect'

const { TextArea } = Input

const CELL_WIDTH = 210

const StatusCell = ({ status, errorMessage, dirtyAfterSuccess }) => {
  if (status === 'submitting') return <Tag color='blue'><Spin size='small' /> Submitting</Tag>
  if (status === 'success' && dirtyAfterSuccess) return <Tag color='orange'>Edited - resubmit</Tag>
  if (status === 'success') return <Tag color='green'>Submitted</Tag>
  if (status === 'failed') return <Tag color='red'>Failed: {errorMessage || 'Unknown error'}</Tag>
  return <Tag color='default'>Pending</Tag>
}

const makeSelect = (options, value, onChange, props = {}) => (
  <Select
    value={value || undefined}
    onChange={onChange}
    style={{ width: '100%', ...(props.style || {}) }}
    allowClear
    {...props}
  >
    {(options || []).map(option => (
      <Select.Option key={option} value={option}>{option}</Select.Option>
    ))}
  </Select>
)

export const makeColumns = ({ lookups, dispatchRowUpdate }) => {
  const updateOperation = (uid, field, value) => dispatchRowUpdate(uid, 'field-operation', field, value)
  const updateDiscipline = (uid, field, value) => dispatchRowUpdate(uid, 'field-discipline', field, value)

  return [
    {
      title: 'Name',
      key: 'name',
      fixed: 'left',
      width: 220,
      render: (_, row) => `${row.firstName} ${row.lastName}`
    },
    { title: 'OP Assessment', key: 'op_assessmentOfPotential', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.aopLookups, row.talentOperationalReview.assessmentOfPotential, value => updateOperation(row.employeeUid, 'assessmentOfPotential', value)) },
    { title: 'OP Skills', key: 'op_skillDevelopmentNeeds', width: CELL_WIDTH + 20, render: (_, row) => makeSelect(lookups.skillLookups, row.talentOperationalReview.skillDevelopmentNeeds, value => updateOperation(row.employeeUid, 'skillDevelopmentNeeds', value), { mode: 'multiple' }) },
    { title: 'OP Nominated', key: 'op_nominatedFor', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.opLookups, row.talentOperationalReview.nominatedFor, value => updateOperation(row.employeeUid, 'nominatedFor', value)) },
    { title: 'OP Talent Pool', key: 'op_talentPoolLevel', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.poolLookups, row.talentOperationalReview.talentPoolLevel, value => updateOperation(row.employeeUid, 'talentPoolLevel', value)) },
    { title: 'OP Flight Risk', key: 'op_isFlightRisk', width: 130, render: (_, row) => <Checkbox checked={row.talentOperationalReview.isFlightRisk} onChange={event => updateOperation(row.employeeUid, 'isFlightRisk', event.target.checked)} /> },
    { title: 'OP Talent Comments', key: 'op_talentReviewComments', width: CELL_WIDTH + 30, render: (_, row) => <TextArea value={row.talentOperationalReview.talentReviewComments} onChange={event => updateOperation(row.employeeUid, 'talentReviewComments', event.target.value)} autoSize={{ minRows: 1, maxRows: 3 }} /> },
    { title: 'OP Manager Comments', key: 'op_managersComments', width: CELL_WIDTH + 30, render: (_, row) => <TextArea value={row.talentOperationalReview.managersComments} onChange={event => updateOperation(row.employeeUid, 'managersComments', event.target.value)} autoSize={{ minRows: 1, maxRows: 3 }} /> },
    { title: 'OP MoR Comments', key: 'op_managerOnceRemovedComments', width: CELL_WIDTH + 30, render: (_, row) => <TextArea value={row.talentOperationalReview.managerOnceRemovedComments} onChange={event => updateOperation(row.employeeUid, 'managerOnceRemovedComments', event.target.value)} autoSize={{ minRows: 1, maxRows: 3 }} /> },
    { title: 'DISC Assessment', key: 'disc_assessmentOfPotential', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.aopLookups, row.talentDisciplineReview.assessmentOfPotential, value => updateDiscipline(row.employeeUid, 'assessmentOfPotential', value)) },
    { title: 'DISC MPA Current', key: 'disc_ultimatePotential', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.lowLookups, row.talentDisciplineReview.ultimatePotential, value => updateDiscipline(row.employeeUid, 'ultimatePotential', value)) },
    { title: 'DISC MPA Potential', key: 'disc_mpaPotential', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.lowLookups, row.talentDisciplineReview.mpaPotential, value => updateDiscipline(row.employeeUid, 'mpaPotential', value)) },
    { title: 'DISC Promotability', key: 'disc_promotability', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.promLookups, row.talentDisciplineReview.promotability, value => updateDiscipline(row.employeeUid, 'promotability', value)) },
    { title: 'DISC Next Move', key: 'disc_nextMove', width: CELL_WIDTH, render: (_, row) => makeSelect(lookups.nextLookups, row.talentDisciplineReview.nextMove, value => updateDiscipline(row.employeeUid, 'nextMove', value)) },
    { title: 'DISC Action', key: 'disc_action', width: CELL_WIDTH + 30, render: (_, row) => <TextArea value={row.talentDisciplineReview.action} onChange={event => updateDiscipline(row.employeeUid, 'action', event.target.value)} autoSize={{ minRows: 1, maxRows: 3 }} /> },
    { title: 'DISC Review Comments', key: 'disc_reviewComments', width: CELL_WIDTH + 30, render: (_, row) => <TextArea value={row.talentDisciplineReview.reviewComments} onChange={event => updateDiscipline(row.employeeUid, 'reviewComments', event.target.value)} autoSize={{ minRows: 1, maxRows: 3 }} /> },
    {
      title: 'Status',
      key: 'status',
      fixed: 'right',
      width: 220,
      render: (_, row) => (
        <Space>
          <StatusCell status={row.status} errorMessage={row.errorMessage} dirtyAfterSuccess={row.dirtyAfterSuccess} />
        </Space>
      )
    }
  ]
}

export const renderExpandedRow = ({ row, dispatchRowUpdate, lookups, fetchUserList }) => {
  const movements = row.talentOperationalMovements || []
  const careerActions = row.talentOperationalReview?.careerActionsDoc || []

  const setMovements = (nextMovements) => dispatchRowUpdate(row.employeeUid, 'field', 'talentOperationalMovements', nextMovements)
  const setCareerActions = (nextActions) => dispatchRowUpdate(row.employeeUid, 'field-operation', 'careerActionsDoc', nextActions)

  const updateMovement = (index, field, value) => {
    const next = [...movements]
    next[index] = { ...(next[index] || {}), [field]: value }
    setMovements(next)
  }

  const updateAction = (index, field, value) => {
    const next = [...careerActions]
    next[index] = { ...(next[index] || {}), [field]: value }
    setCareerActions(next)
  }

  return (
    <div className='w-full'>
      <p className='font-semibold mb-2'>Succession movements</p>
      <Space direction='vertical' className='w-full'>
        {movements.map((movement, index) => (
          <div key={`${row.employeeUid}-movement-${index}`} className='flex flex-wrap items-start gap-2'>
            {makeSelect(['promotion', 'linearMove'], movement?.movementType, value => updateMovement(index, 'movementType', value), { placeholder: 'Type', style: { minWidth: 160 } })}
            {makeSelect(lookups.promLookups, movement?.time, value => updateMovement(index, 'time', value), { placeholder: 'Timeline', style: { minWidth: 200 } })}
            {makeSelect(lookups.nextLookups, movement?.nextMove, value => updateMovement(index, 'nextMove', value), { placeholder: 'Next move', style: { minWidth: 250 }, showSearch: true })}
            <Button danger icon={<MinusCircleOutlined />} onClick={() => setMovements(movements.filter((_, idx) => idx !== index))} />
          </div>
        ))}
      </Space>
      <Button className='mt-2' icon={<PlusOutlined />} onClick={() => setMovements([...movements, { movementType: '', time: '', nextMove: '' }])}>
        Add movement
      </Button>

      <p className='font-semibold mt-5 mb-2'>Career actions</p>
      <Space direction='vertical' className='w-full'>
        {careerActions.map((action, index) => (
          <div key={`${row.employeeUid}-action-${index}`} className='flex flex-wrap items-start gap-2'>
            <Input value={action?.action || ''} onChange={event => updateAction(index, 'action', event.target.value)} placeholder='Action' style={{ minWidth: 240 }} />
            <DatePicker
              value={action?.date ? (dayjs.isDayjs(action.date) ? action.date : dayjs(action.date)) : null}
              onChange={(value) => updateAction(index, 'date', value)}
              style={{ minWidth: 160 }}
            />
            <DebouncedSelect
              showSearch
              placeholder='Person Responsible'
              fetchOptions={fetchUserList}
              value={action?.responsibility ? { label: action.responsibility, value: action.responsibility } : undefined}
              onChange={(selected) => updateAction(index, 'responsibility', selected?.value || '')}
              style={{ minWidth: 220 }}
            />
            <Button danger icon={<MinusCircleOutlined />} onClick={() => setCareerActions(careerActions.filter((_, idx) => idx !== index))} />
          </div>
        ))}
      </Space>
      <Button className='mt-2' icon={<PlusOutlined />} onClick={() => setCareerActions([...careerActions, { action: '', date: null, responsibility: '' }])}>
        Add action
      </Button>
    </div>
  )
}

